// Error types
export enum GetSchedulingPractitionersErrors {
  NO_PROVIDERS = "no_providers",
  NO_REFERRAL_FOUND_FOR_PATIENT = "no_referral_found_for_patient",
  GENERIC = "generic",
}

export enum GetSchedulingLocationsErrors {
  NO_LOCATIONS = "no_locations",
  GENERIC = "generic",
}

export enum CheckAvailabilityErrors {
  INVALID_PARAMS = "invalid_params",
  HEALOW_VISIT_TYPE_NOT_SET = "healow_visit_type_not_set",
  HEALOW_PRACTITIONER_EHR_ID_NOT_SET = "healow_practitioner_ehr_id_not_set",
  HEALOW_LOCATION_EHR_ID_NOT_SET = "healow_location_ehr_id_not_set",
  GENERIC = "generic",
}

export enum BookAppointmentErrors {
  APPOINTMENT_SLOT_NOT_AVAILABLE = "appointment_slot_not_available",
  APPOINTMENT_PARSING_FAILED = "appointment_parsing_failed",
  GENERIC = "generic",
}

type SchedulingErrorCode =
  | GetSchedulingPractitionersErrors
  | GetSchedulingLocationsErrors
  | CheckAvailabilityErrors
  | BookAppointmentErrors;

// Client-side error messages
const SUPPORT_MESSAGE = "Please reach out to support.";

export const GetSchedulingPractitionersClientErrorMessages = {
  [GetSchedulingPractitionersErrors.NO_PROVIDERS]: "No valid providers found for the most recent referral.",
  [GetSchedulingPractitionersErrors.NO_REFERRAL_FOUND_FOR_PATIENT]:
    "There is no referral found for the patient, so an appointment cannot be scheduled.",
  [GetSchedulingPractitionersErrors.GENERIC]: "There was an error fetching providers. Please refresh the page.",
};

export const GetSchedulingLocationsClientErrorMessages = {
  [GetSchedulingLocationsErrors.NO_LOCATIONS]: `No locations configured for scheduling for customer. ${SUPPORT_MESSAGE}`,
  [GetSchedulingLocationsErrors.GENERIC]: "There was an error fetching locations. Please refresh the page.",
};

export const CheckAvailabilityClientErrorMessages = {
  [CheckAvailabilityErrors.INVALID_PARAMS]: "Invalid parameters provided. Please check the form and try again.",
  [CheckAvailabilityErrors.HEALOW_VISIT_TYPE_NOT_SET]: `Healow visit type is not configured for scheduling. ${SUPPORT_MESSAGE}`,
  [CheckAvailabilityErrors.HEALOW_PRACTITIONER_EHR_ID_NOT_SET]: `At least one of the selected practitioners' EHR ID is not configured for scheduling. ${SUPPORT_MESSAGE}`,
  [CheckAvailabilityErrors.HEALOW_LOCATION_EHR_ID_NOT_SET]: `At least one of the selected locations' EHR ID is not configured for scheduling. ${SUPPORT_MESSAGE}`,
  [CheckAvailabilityErrors.GENERIC]: "There was an error checking availability. Please try again.",
};

export const BookAppointmentClientErrorMessages = {
  [BookAppointmentErrors.APPOINTMENT_SLOT_NOT_AVAILABLE]:
    "The selected appointment slot is not available. Please select a different slot.",
  [BookAppointmentErrors.APPOINTMENT_PARSING_FAILED]:
    "Failed to parse Appointment response from Healow. Please check on eCW if appointment was booked successfully.",
  [BookAppointmentErrors.GENERIC]: "Please go through the EMR directly to book the appointment.",
};

// Error domains
export enum SchedulingErrorDomain {
  GET_SCHEDULING_PRACTITIONERS = "get_scheduling_practitioners",
  GET_SCHEDULING_LOCATIONS = "get_scheduling_locations",
  CHECK_AVAILABILITY = "check_availability",
  BOOK_APPOINTMENT = "book_appointment",
}

// Map error domains to error codes and messages
export const schedulingErrorConfig = {
  [SchedulingErrorDomain.GET_SCHEDULING_PRACTITIONERS]: {
    enum: GetSchedulingPractitionersErrors,
    messages: GetSchedulingPractitionersClientErrorMessages,
  },
  [SchedulingErrorDomain.GET_SCHEDULING_LOCATIONS]: {
    enum: GetSchedulingLocationsErrors,
    messages: GetSchedulingLocationsClientErrorMessages,
  },
  [SchedulingErrorDomain.CHECK_AVAILABILITY]: {
    enum: CheckAvailabilityErrors,
    messages: CheckAvailabilityClientErrorMessages,
  },
  [SchedulingErrorDomain.BOOK_APPOINTMENT]: {
    enum: BookAppointmentErrors,
    messages: BookAppointmentClientErrorMessages,
  },
};

// SchedulingError class
export class SchedulingError extends Error {
  constructor(
    public domain: SchedulingErrorDomain,
    public code: SchedulingErrorCode
  ) {
    super(SchedulingError.getMessage(domain, code));

    this.name = "SchedulingError";
  }

  private static getMessage(domain: SchedulingErrorDomain, code: SchedulingErrorCode) {
    const config = schedulingErrorConfig[domain];

    if (config) {
      return (config.messages as Record<string, string>)?.[code] ?? "Unexpected scheduling error";
    }

    return "Unknown scheduling error domain";
  }
}

/**
 * Creates a SchedulingError by inspecting a caught error object.
 * * If the server provides a valid error code, it creates a specific error.
 * Otherwise, it falls back to the GENERIC error for the given domain.
 *
 * @param caughtError The error object from a catch block.
 * @param domain The scheduling domain where the error occurred.
 * @returns A SchedulingError instance.
 */
export function createApiError(caughtError: unknown, domain: SchedulingErrorDomain): SchedulingError {
  if (caughtError instanceof SchedulingError) {
    return caughtError;
  }

  const { enum: errorEnum } = schedulingErrorConfig[domain];

  let serverCode: string | undefined;

  if (caughtError instanceof Error && typeof caughtError.message === "string") {
    try {
      // First, attempt to parse the message as JSON, e.g., '{"error":"no_providers"}'
      // This handles FetchErrors (e.g. through queries)
      const parsedMessage = JSON.parse(caughtError.message);

      if (typeof parsedMessage.error === "string") {
        serverCode = parsedMessage.error;
      }
    } catch (e) {
      // If parsing fails, assume the message is the error code
      serverCode = caughtError.message;
    }
  }

  if (serverCode && Object.values(errorEnum).includes(serverCode)) {
    return new SchedulingError(domain, serverCode as SchedulingErrorCode);
  }

  return new SchedulingError(domain, errorEnum.GENERIC);
}
